import { useCallback, useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { DragDropContext } from "@hello-pangea/dnd";
import { Switch } from "@mui/joy";
import { useSelector } from "react-redux";
import GetQrCodeModal from "./ModalComponents/GetQrCodeModal";
import MenuItemHeader from "./MenuComponents/MenuItemHeader";
import MenuSectionHeader from "./MenuComponents/MenuSectionHeader";
import MenuSections from "./MenuComponents/MenuSections";
import { saveMenu, fetchMenuById } from "../api/menuApi";
import { uploadModel } from "../api/modelApi";
import CustomerMenu from "./CustomerMenuComponents/CustomerMenu";
import "../css/Menu.css";

/**
 * Menu component that allows users to view and edit menu sections and items.
 *
 * @component
 * @param {Object} props - The component props.
 * @param {Array} props.sections - Initial sections to display in the menu.
 *
 * @example
 * const sections = [
 *   { id: 1, name: 'Appetizers', items: [{ id: 1, name: 'Spring Rolls', price: 5.99, description: 'Crispy rolls' }] },
 *   { id: 2, name: 'Main Course', items: [{ id: 2, name: 'Steak', price: 19.99, description: 'Grilled steak' }] }
 * ];
 * <Menu sections={sections} />
 *
 * @returns {JSX.Element} The rendered Menu component.
 */
const Menu = () => {
  const [sections, setSections] = useState([]); // Initialize with an empty array
  const [editMode, setEditMode] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const frontendPath = window.location.pathname;
  const { restaurantId, menuId } = useParams();
  const navigate = useNavigate();
  const isMobile = useSelector((state) => state.mobile.isMobile);

  useEffect(() => {
    // Load the default menu on component mount
    const fetchMenu = async () => {
      try {
        const menuData = await fetchMenuById(menuId, frontendPath);
        menuData.sections.forEach((section) => {
          section.items = section.items.filter((item) => item !== null);
        });
        setSections(menuData.sections);
      } catch (error) {
        console.error("Error fetching menu:", error);
        navigate("/404");
      }
    };

    fetchMenu();
  }, [frontendPath, menuId, navigate]);

  const addSection = useCallback(() => {
    const newId =
      sections.length > 0
        ? Math.max(...sections.map((section) => section.id)) + 1
        : 0;
    setSections([...sections, { name: "new section", items: [], id: newId }]);
  }, [sections]);

  const deleteSection = useCallback(
    (targetSection) => {
      setSections(sections.filter((section) => section !== targetSection));
    },
    [sections]
  );

  const updateSection = useCallback(
    (id, name) => {
      const updatedSections = sections.map((section) =>
        section.id === id ? { ...section, name: name } : section
      );
      setSections(updatedSections);
    },
    [sections]
  );

  const addItem = useCallback(
    ({
      name,
      price,
      description,
      dietaryLabels,
      foodType,
      autoScale,
      generateModel,
      modelFile,
      flatModelFile,
      height,
      depth,
      width,
    }) => {
      // Find the maximum ID across all items in all sections
      const maxId = sections.reduce((maxId, section) => {
        const sectionMaxId =
          section.items.length > 0
            ? Math.max(...section.items.map((item) => item.id))
            : 0; // If no items in section, consider max ID as 0
        return Math.max(maxId, sectionMaxId);
      }, 0);

      // Create a new item with the incremented ID
      const newItem = {
        name: name,
        price: price,
        description: description,
        id: maxId + 1,
        dietaryLabels: dietaryLabels,
        foodType: foodType,
        autoScale: autoScale,
        generateModel: generateModel,
        modelFile: modelFile || null,
        flatModelFile: flatModelFile,
        height: height,
        depth: depth,
        width: width,
      };

      // Assuming you want to add the new item to the first section
      const section = sections[0];
      const newItems = [...section.items, newItem];

      // Update the sections with the new item added
      const updatedSections = sections.map((s) =>
        s === section ? { ...s, items: newItems } : s
      );

      setSections(updatedSections);
    },
    [sections]
  );

  const updateItem = useCallback(
    (section, item) => {
      const updatedItems = section.items.map((i) =>
        i.id === item.id ? { ...i, ...item } : i
      );
      const updatedSections = sections.map((s) =>
        s === section ? { ...s, items: updatedItems } : s
      );
      setSections(updatedSections);
    },
    [sections]
  );

  const deleteItem = useCallback(
    (section, targetItem) => {
      const updatedItems = section.items.filter((item) => item !== targetItem);
      const updatedSections = sections.map((s) =>
        s === section ? { ...s, items: updatedItems } : s
      );
      setSections(updatedSections);
    },
    [sections]
  );

  const handleItemReorder = useCallback(
    (section, newItems) => {
      const updatedSections = sections.map((s) =>
        s === section ? { ...s, items: newItems } : s
      );
      setSections(updatedSections);
    },
    [sections]
  );

  const getIdFromDroppableId = (droppableId) => {
    return parseInt(droppableId.split("-")[1]);
  };

  const handleDragEnd = useCallback(
    (reOrderObject) => {
      const { source, destination } = reOrderObject;
      if (!destination) return;

      if (destination.droppableId === "sections") {
        reorderSections(source.index, destination.index);
      } else if (source.droppableId === destination.droppableId) {
        reorderItemsWithinSection(source, destination);
      } else {
        moveItemBetweenSections(source, destination);
      }
    },
    [sections]
  );

  const reorderSections = (sourceIndex, destinationIndex) => {
    const reorderedSections = Array.from(sections);
    const [movedItem] = reorderedSections.splice(sourceIndex, 1);
    reorderedSections.splice(destinationIndex, 0, movedItem);
    setSections(reorderedSections);
  };

  const reorderItemsWithinSection = (source, destination) => {
    const sectionId = getIdFromDroppableId(source.droppableId);
    const section = sections.find((s) => s.id === sectionId);
    const reorderedItems = Array.from(section.items);
    const [movedItem] = reorderedItems.splice(source.index, 1);
    reorderedItems.splice(destination.index, 0, movedItem);

    const updatedSections = sections.map((s) =>
      s === section ? { ...s, items: reorderedItems } : s
    );
    setSections(updatedSections);
  };

  const moveItemBetweenSections = (source, destination) => {
    const sourceSectionId = getIdFromDroppableId(source.droppableId);
    const destinationSectionId = getIdFromDroppableId(destination.droppableId);
    const sourceSection = sections.find((s) => s.id === sourceSectionId);
    const destinationSection = sections.find(
      (s) => s.id === destinationSectionId
    );

    const sourceItems = Array.from(sourceSection.items);
    const destinationItems = Array.from(destinationSection.items);

    const [movedItem] = sourceItems.splice(source.index, 1);
    destinationItems.splice(destination.index, 0, movedItem);

    const updatedSections = sections.map((s) => {
      if (s === sourceSection) {
        return { ...s, items: sourceItems };
      } else if (s === destinationSection) {
        return { ...s, items: destinationItems };
      } else {
        return s;
      }
    });

    setSections(updatedSections);
  };

  const onMenuSave = async () => {
    if (!restaurantId) {
      console.error("Restaurant ID is missing. Cannot save menu.");
      return;
    }
    setIsSaving(true);
    // Create a deep copy of the sections to avoid mutating state directly
    const updatedSections = await Promise.all(
      sections.map(async (section) => ({
        ...section,
        items: await Promise.all(
          section.items.map(async (item) => {
            // Upload the model file and link the response to the item
            try {
              if (!item.model) {
                const model = await uploadModel(
                  restaurantId,
                  item.modelFile,
                  item.flatModelFile
                );
                return {
                  ...item,
                  model, // Link the uploaded model to the item
                };
              }
            } catch (error) {
              console.error(
                `Failed to upload model for item ${item.name}:`,
                error
              );
            }
            return item;
          })
        ),
      }))
    );

    const completeMenu = {
      id: menuId,
      sections: updatedSections.map((section) => ({
        id: section.id || null,
        name: section.name,
        items: section.items.map((item) => ({
          id: item.id || null,
          name: item.name,
          price: item.price,
          description: item.description,
          foodType: item.foodType,
          autoScale: item.autoScale,
          dietaryLabels: item.dietaryLabels,
          generateModel: item.generateModel,
          height: item.height,
          depth: item.depth,
          width: item.width,
          model: item.model,
        })),
      })),
    };

    try {
      await saveMenu(completeMenu, window.location.pathname, restaurantId); // Save the entire menu object
    } catch (error) {
      console.error("Failed to save menu:", error);
    } finally {
      setIsSaving(false);
    }
  };

  const onMenuCancel = () => {
    //TODO implement cancel menu functionality
  };

  const [isGetQrCodeOpen, setIsGetQrCodeOpen] = useState(false);
  const openGetQrCode = useCallback(() => setIsGetQrCodeOpen(true), []);
  const closeGetQrCode = useCallback(() => setIsGetQrCodeOpen(false), []);

  const restaurant = JSON.parse(localStorage.getItem("restaurant"));
  const restaurantName = restaurant?.name || "no restaurant name provided";
  const restaurantURL =
    process.env.REACT_APP_URL_FRONTEND + `/menu/${restaurant.menu.id}`;

  return (
    <div className="menu-container">
      {isSaving && <div className="saving-banner">Saving menu...</div>}
      <div className="Menu">
        <div className="menu-header">
          <h1 className="menu-text">menu</h1>
          <div className="menu-header-btns">
            <button className="menu-get-qr-code-btn" onClick={openGetQrCode}>
              get qr code
            </button>
            <div className="menu-toggle">
              <span className="menu-edit-text">
                {editMode ? "edit mode" : "view mode"}{" "}
              </span>
              <Switch
                className="menu-toggle-switch"
                checked={editMode}
                onChange={(event) => setEditMode(event.target.checked)}
              />
            </div>
          </div>
        </div>
        {/* Show CustomerMenu when in View Mode, otherwise show the editable sections */}
        {editMode ? (
          <DragDropContext onDragEnd={handleDragEnd}>
            <MenuSections
              sections={sections}
              onSectionDelete={deleteSection}
              onUpdateSection={updateSection}
              onItemUpdate={updateItem}
              onItemDetete={deleteItem}
              handleItemReorder={handleItemReorder}
              editMode={editMode}
            />
            {editMode && (
              <div className="menu-edit-headers">
                <MenuSectionHeader onSectionClick={addSection} />
                {sections.length !== 0 && (
                  <MenuItemHeader onNewItem={addItem} />
                )}
              </div>
            )}
          </DragDropContext>
        ) : (
          <CustomerMenu menuId={menuId} inAdmin={true} />
        )}
        <div className="footer">
          {editMode && (
            <div className="footer-buttons">
              <button className="cancel-menu-button" onClick={onMenuCancel}>
                cancel menu
              </button>
              <button className="save-menu-button" onClick={onMenuSave}>
                save menu
              </button>
            </div>
          )}
        </div>
        {isGetQrCodeOpen && (
          <GetQrCodeModal
            className="menu-get-qr-code-modal"
            isOpen={isGetQrCodeOpen}
            onRequestClose={closeGetQrCode}
            width={isMobile ? "65%" : "35%"}
            restaurantName={restaurantName}
            restaurantURL={restaurantURL}
          />
        )}
      </div>
    </div>
  );
};

export default Menu;
